<template>
  <div class="product-item">
    <div class="product-item--body flex-grow-1 d-flex">
      <div class="product-item--image">
        <img :src="`${product.showimg_url}?tr=w-200,h-200,cm-pad_resize,bg-FFFFFF`" />
      </div>
      <div class="product-item--content">
        <div class="title">{{ product.title }}</div>

        <div class="description">{{ product.short_desc }}</div>

        <div class="sku">
          <div> SKU: {{ catalogue.sku }} </div>
        </div>

        <div v-for="(variant, key) in variation" :key="key" class="variation">
          <div class="variation-title">{{ variant.title }}</div>
          <div class="variation-items">
            <div
              v-for="item in variant.items"
              :key="item"
              :class="{ active: item === selected[key]}"
              @click="selected[key] = item"
            >
              {{ item }}
            </div>
          </div>
        </div>

        <div class="variation">
          <div class="variation-title">Unit</div>
          <div class="variation-items">
            <div
              v-for="item in priceList"
              :key="item.uom"
              :class="{ active: item.uom === price.uom }"
              @click="price = item"
            >
              {{ item.uom }}
            </div>
          </div>
          <div class="text-muted mt-1" v-if="price.uom !== 'PCS'"> 1 {{ price.uom }} {{ price.numerator }} PCS </div>
        </div>
      </div>
    </div>
    <div class="product-item--footer">
      <div>
        <div v-if="price.price_selling >= discountedPrice" class="price disc">
          {{ discountedText }}
        </div>
        <div v-if="price.price_selling >= discountedPrice" class="price slash">
          {{ price.price_selling | currency }}
        </div>
        <div class="price">{{ discountedPrice | currency }} <span style="font-size: 0.8rem; color: #999">/ {{ price.uom }}</span></div>
      </div>
    </div>
  </div>
</template>

<script>
import { getOptions } from '@/services/product'
export default {
  props: {
    product: {
      type: Object,
    },
  },
  data() {
    return {
      selected: {
        color: '',
        size: '',
        flavour: '',
      },
      unit: null,
      price: {},
      discountText: '',
    }
  },
  computed: {
    /**
     * @returns {any[]}
     */
    catalogues() {
      return this.product.catalog_list
    },

    /**
     * @returns {string[]}
     */
    colorOptions() {
      return Object.keys(getOptions(this.catalogues || [], 0))
    },

    /**
     * @returns {string[]}
     */
    sizeOptions() {
      return Object.keys(getOptions(this.catalogues || [], 1))
    },

    /**
     * @returns {string[]}
     */
    flavorOptions() {
      return Object.keys(getOptions(this.catalogues || [], 2))
    },

    /**
     * @typedef {{title: string, items: string[]}} Variation
     * @returns {{ color?: Variation, size?: Variation, flavour?: Variation }}
     */
    variation() {
      const variation = {}

      const addVariant = (key, title, items) => {
        if (items.length) {
          variation[key] = {
            title,
            items,
          }
        }
      }

      addVariant('color', 'Color', this.colorOptions)
      addVariant('size', 'Size', this.sizeOptions)
      addVariant('flavour', 'Flavour', this.flavorOptions)

      return variation
    },

    /**
     * @returns {object | undefined}
     */
    catalogue() {
      return this.catalogues.find((catalogue) => Object.values(this.selected).join('__') === catalogue.option.join('__'))
    },

    /**
     * @returns {any[]}
     */
    priceList() {
      return this.catalogue?.price_list || []
    },

    /**
     * @returns {number}
     */
    discountedPrice() {
      if (!this.price) return 0

      let price = this.price.price_selling || 0
      const levelPrices = this.price.level_price || []
      const { discount_type, discount_value } = levelPrices.length ? levelPrices[0] : {}

      if (discount_type === 1) {
        price = price * (100 - discount_value) / 100
      } else if (discount_type === 2) {
        price = price - discount_value
      }

      return price
    },
    discountedText () {
      if (!this.price) return null

      const levelPrices = this.price.level_price || []
      const { discount_type, discount_value } = levelPrices.length ? levelPrices[0] : {}

      if (discount_type === 1) {
        return `Discount ${discount_value} %`
      } else if (discount_type === 2) {
        return `Discount Rp. ${discount_value}`
      } else {
        return null
      }
    },
  },
  watch: {
    catalogue: {
      immediate: true,
      handler() {
        this.price = this.priceList[0] || {}
      },
    },
  },
  mounted() {
    this.selected.color = this.colorOptions[0] || ''
    this.selected.size = this.sizeOptions[0] || ''
    this.selected.flavour = this.flavorOptions[0] || ''
  },
}
</script>

<style lang="scss" scoped>
.product-item {
  margin-bottom: 1.5rem;
  border: 1px solid #e4e9f0;
  padding: .75rem;
  display: flex;
  flex-wrap: wrap;
  background: #fff;

  @media (min-width: 576px) {
    flex-wrap: nowrap;
  }

  &--image {
    margin-right: 1rem;

    img {
      width: 75px;

      @media (min-width: 992px) {
        width: 100px;
      }
    }
  }

  &--body {
    width: 100%;
    @media (min-width: 576px) {
      width: auto;
      flex-grow: 1;
    }
  }

  &--footer {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    width: 100%;
    margin: 1rem 0;
    justify-content: flex-end;

    @media (min-width: 576px) {
      max-width: 170px;
    }
  }

  &--content {
    flex-grow: 1;
    width: auto;
  }

  > div:nth-child(0) {
    width: 100%;
  }

  .title {
    line-height: 1.2;
    font-weight: bold;
    font-size: 1.15rem;
    margin-bottom: .25rem;
  }

  .description {
    font-size: 85%;
    color: #a7a9c9;
    margin-bottom: .5rem;
  }

  .sku {
    font-size: 85%;
    color: #a7a9c9;
    margin-bottom: .5rem;
  }

  .price {
    color: #595c97;
    font-size: 1.25rem;
    width: 100%;
    text-align: right;

    &.slash {
      color: #a7a9c9;
      font-size: .9rem;
      text-decoration-line: line-through;
      // margin-bottom: -.5rem;
      margin-bottom: .1rem;
    }

    &.disc {
      color: #a7a9c9;
      font-size: .9rem;
      // margin-bottom: -.5rem;
      margin-bottom: .1rem;
    }
  }
}

.variation {
  margin-bottom: 1rem;
  font-size: 90%;

  &-title {
    // margin-bottom: .25rem;
  }

  &-items {
    display: flex;

    > div {
      cursor: pointer;
      border: 1px solid #e4e9f0;
      padding: .05rem .5rem;
      font-size: 90%;

      &:not(:last-child) {
        margin-right: .5rem;
      }

      &.active {
        background-color: #fb7b2f;
        color: #fff;
      }
    }
  }
}
</style>
